import type { Character } from '../domain/character.ts';
import type { InventoryCatalogue, InventoryEntry } from './inventory.ts';
import { equipProblem, unallocatedCopies } from './inventory.ts';
import type { Hotlist, HotlistEntry } from './hotlist.ts';
import { allocationProblem, hotlistHomeForReturn, hotlistSlotIndexProblem } from './hotlist.ts';

/**
 * RULE DCC-HOT-011 - the free weapon swap, as part of the Attack Action.
 *
 * Rules p.34: "The crawler can swap the weapon in their hand for another weapon
 * from their Hotlist as part of the Attack Action." Rules p.48: "If a crawler
 * swaps their currently equipped weapon to another weapon on their Hotlist,
 * they swap for free as part of the Attack Action."
 *
 * RULE DCC-HOT-012 - it is an EXCHANGE, not a draw and a drop. The worked
 * example on Rules p.40 is explicit: Park "swap[s] to his Dagger, putting his
 * Shotgun back into the Hotlist as part of the Attack." C&C p.15 says the same
 * mechanically: "The weapon you're wielding temporarily moves into the Hotlist
 * while the new weapon moves out, and you roll an attack with the new weapon.
 * You only spend one Action on this swap and attack."
 *
 * That last sentence is also why this is Hotlist-only. C&C p.15 continues: "If
 * you'd needed a weapon from your Inventory instead of your Hotlist, you
 * would've needed to spend two Actions." The Hotlist is the free route; the
 * Inventory is not, and this file must never offer one.
 *
 * COSTS NOTHING EXTRA. The swap rides on the Attack's single Action. Nothing
 * here touches the Action pool - `resolveAttackAction` still spends exactly one
 * Action, in the same place, in the same order.
 */

/** RULE DCC-HOT-013 - every printed statement says *weapon*. A shield or torch
 *  is swapped under DCC-HOT-008, which costs the Action, and is not this. */
export interface WeaponSwapPlan {
  /** The Hotlist slot the incoming weapon comes out of. */
  slot: number;
  /** What is being drawn. */
  incomingItemId: string;
  /**
   * The Attack the drawn weapon provides.
   *
   * C&C p.15 settles which Attack a swap Attack is: "the weapon you're wielding
   * moves into the Hotlist while the new weapon moves out, and you roll an
   * attack WITH THE NEW WEAPON." So when a swap is requested the Attack is not
   * a free choice - it is this one. Exposed here so the caller can hold itself
   * to it rather than re-deriving the link.
   */
  incomingAttackId: string;
  /** What goes back, or null when the hand was empty (a draw, not a swap). */
  outgoingItemId: string | null;
  /** Where the outgoing weapon lands, or null when there is nothing to stow. */
  outgoingSlot: number | null;
}

export type WeaponSwapOutcome =
  | { ok: true; character: Character; plan: WeaponSwapPlan }
  | { ok: false; reason: string };

/** The Item a Hotlist slot names, when that slot holds an Item at all. */
function itemEntryAt(hotlist: Hotlist, slot: number): { itemId: string; quantity: number } | null {
  const entry = hotlist[slot] ?? null;
  if (entry === null || entry.kind !== 'item') return null;
  return { itemId: entry.itemId, quantity: entry.quantity };
}

/** Whether an Item is a physical weapon: it names an Attack this build knows. */
export function isPhysicalWeapon(itemId: string, catalogue: InventoryCatalogue): boolean {
  const item = catalogue.lookupItem(itemId);
  if (!item || item.weaponAttackId === null) return false;
  return catalogue.lookupAttack(item.weaponAttackId) !== undefined;
}

/** The equipped held weapons, which is what a swap replaces. */
function equippedWeapons(
  inventory: readonly InventoryEntry[],
  catalogue: InventoryCatalogue,
): InventoryEntry[] {
  return inventory.filter((entry) => {
    if (entry.equipped <= 0) return false;
    const item = catalogue.lookupItem(entry.itemId);
    return item !== undefined && item.slot === 'hands' && isPhysicalWeapon(entry.itemId, catalogue);
  });
}

/**
 * Works out the swap and returns the crawler mid-Attack, or refuses.
 *
 * PURE. It writes nothing, spends nothing, and rolls nothing. The caller feeds
 * the result into the ordinary Attack pathway, which is what actually costs the
 * Action.
 *
 * Legality is asked of the EXISTING rules rather than re-decided here. In
 * particular the incoming weapon must pass `equipProblem` against the inventory
 * as it will be once the outgoing weapon is put away, so a two-handed weapon
 * still cannot be drawn while a Shield occupies the other hand (RULE
 * DCC-INV-016). The books never address that case for the free swap - recorded
 * as an open silence - and refusing is the conservative reading already adopted
 * elsewhere, arrived at by asking the Gear Slot rules rather than by inventing
 * a swap-specific one.
 */
export function planWeaponSwap(
  character: Character,
  slot: number,
  catalogue: InventoryCatalogue,
): WeaponSwapOutcome {
  const slotProblem = hotlistSlotIndexProblem(slot);
  if (slotProblem) return { ok: false, reason: slotProblem };

  const incoming = itemEntryAt(character.hotlist, slot);
  if (incoming === null) {
    return { ok: false, reason: `Hotlist slot ${slot + 1} does not hold an Item.` };
  }

  const incomingItem = catalogue.lookupItem(incoming.itemId);
  if (!incomingItem) {
    return {
      ok: false,
      reason: `Hotlist slot ${slot + 1} names an Item this installation does not have.`,
    };
  }
  // RULE DCC-HOT-013 - the free swap is weapon-specific. Every printed
  // statement says *weapon*; a shield or a torch is swapped under DCC-HOT-008,
  // which costs an Action and is built (E5, `rules/hotlistUse.ts`).
  if (!isPhysicalWeapon(incoming.itemId, catalogue)) {
    return {
      ok: false,
      reason: `${incomingItem.name} is not a weapon. Only a weapon swaps free as part of the Attack (Rules p.48).`,
    };
  }

  const inventory = character.inventory;
  const owned = inventory.find((e) => e.itemId === incoming.itemId);
  // RULE DCC-HOT-002 - the slot allocates real copies, and the WHOLE allocation
  // must still be backed, not merely one of them.
  //
  // ONE COPY IS NOT THE TEST. E2 deliberately keeps an over-allocated slot
  // loadable, visible and INERT - a slot claiming two Daggers when the crawler
  // owns one shows as `unavailable` and does nothing. Asking only whether a
  // single copy is free would let E3 execute exactly the entry E2 promised was
  // inert, and drawing from it would leave `equipped + held + hotlisted` at two
  // against one owned: the invariant still broken, and now broken by an
  // operation rather than by history.
  //
  // Not repaired here either. Shrinking the slot or unequipping something to
  // make the numbers work would edit what the player did not ask to change;
  // they correct the allocation out of combat, through the configuration and
  // Inventory pathways that already exist.
  // RULE DCC-INV-018 - a copy in a hand cannot also back this slot.
  const available = owned === undefined ? 0 : unallocatedCopies(owned);
  if (available < incoming.quantity) {
    return {
      ok: false,
      reason:
        `Hotlist slot ${slot + 1} claims ${incoming.quantity} ${incomingItem.name} but only ` +
        `${available} ${available === 1 ? 'is' : 'are'} free. Sort the Hotlist out before the ` +
        'fight; a slot that does not match what the crawler carries cannot be used.',
    };
  }

  const held = equippedWeapons(inventory, catalogue);
  if (held.length > 1) {
    // Two weapons in hand and nothing says which one is put away. Refused
    // rather than picked for the player.
    return {
      ok: false,
      reason:
        'This crawler is holding more than one weapon, and the rules do not say which one a ' +
        'free swap puts away. Unequip one first.',
    };
  }
  const outgoing = held[0] ?? null;
  if (outgoing && outgoing.itemId === incoming.itemId) {
    return { ok: false, reason: `${incomingItem.name} is already in hand.` };
  }

  // Put the outgoing weapon away FIRST, in memory, so the hand it frees is
  // available to the incoming one. A one-handed-for-two-handed swap depends on
  // this ordering, and doing it the other way round would refuse the printed
  // Shotgun-to-Dagger example's mirror image for no reason.
  let working = inventory;
  let outgoingSlot: number | null = null;
  let hotlist: Hotlist = [...character.hotlist];

  if (outgoing) {
    const home = hotlistHomeForReturn(hotlist, slot, incoming.quantity, outgoing.itemId);
    if ('reason' in home) return { ok: false, reason: home.reason };

    working = working.map((entry) =>
      entry.itemId === outgoing.itemId ? { ...entry, equipped: entry.equipped - 1 } : entry,
    );

    if (home.merge) {
      const existing = hotlist[home.slot] ?? null;
      if (existing !== null && existing.kind === 'item') {
        hotlist[home.slot] = { ...existing, quantity: existing.quantity + 1 };
      }
    } else {
      hotlist[home.slot] = { kind: 'item', itemId: outgoing.itemId, quantity: 1 };
    }
    outgoingSlot = home.slot;
  }

  // The incoming weapon leaves the Hotlist. One copy moves, per the exchange.
  if (incoming.quantity === 1) {
    // Emptied - unless the outgoing weapon just took this slot.
    if (outgoingSlot !== slot) hotlist[slot] = null;
  } else {
    const entry: HotlistEntry = {
      kind: 'item',
      itemId: incoming.itemId,
      quantity: incoming.quantity - 1,
    };
    hotlist[slot] = entry;
  }

  // Asked of the SHARED rule, against the inventory as it will be. This is what
  // refuses a two-handed weapon while a Shield is held (RULE DCC-INV-016).
  const cannotEquip = equipProblem(working, incoming.itemId, catalogue);
  if (cannotEquip !== null) return { ok: false, reason: cannotEquip };

  working = working.map((entry) =>
    entry.itemId === incoming.itemId ? { ...entry, equipped: entry.equipped + 1 } : entry,
  );

  // THE INVARIANT, checked on the state this swap would actually produce, for
  // the two Items it touches and no others. An unrelated potion slot that is
  // over-allocated is somebody else's problem and must not block a legal swap.
  //
  // The outgoing side needs this as much as the incoming one: a weapon that is
  // held AND already Hotlisted is another state E2 preserves, and putting it
  // away would raise its stack to two against one owned - worsening an
  // impossible allocation rather than merely carrying it.
  for (const touched of [incoming.itemId, outgoing?.itemId]) {
    if (touched === undefined) continue;
    const entry = working.find((e) => e.itemId === touched);
    if (entry === undefined) continue;
    const name = catalogue.lookupItem(touched)?.name ?? touched;
    const problem = allocationProblem(hotlist, touched, name, entry.quantity, entry.equipped, entry.held);
    if (problem !== null) return { ok: false, reason: problem };
  }

  return {
    ok: true,
    character: { ...character, inventory: working, hotlist },
    plan: {
      slot,
      incomingItemId: incoming.itemId,
      // Non-null: `isPhysicalWeapon` above already proved the link resolves.
      incomingAttackId: incomingItem.weaponAttackId as string,
      outgoingItemId: outgoing?.itemId ?? null,
      outgoingSlot,
    },
  };
}

/**
 * What the player picked from the swap menu.
 *
 * THE ITEM, NOT THE SLOT. A slot number alone is not a choice: it is a position
 * whose contents change, and the swap itself is what changes them. After a
 * successful swap the drawn weapon is in hand and the OUTGOING one is sitting
 * in the slot that was chosen - so a remembered slot number now names a
 * different object.
 *
 * That is invisible when the two weapons share an Attack profile, which is
 * exactly what custom content is for: a Fine Dagger and a Flaming Dagger both
 * link to `dagger` and differ only in their modifiers. Remembering "slot 3"
 * would leave the selection legal, leave it in the menu, and quietly turn
 * "draw the Flaming Dagger" into "draw the Fine Dagger" - offering to undo the
 * swap the player just made, and previewing the weapon they had put away.
 *
 * `weaponAttackId` is NOT the identity either, for the same reason: it is the
 * thing the two weapons have in common, not what tells them apart.
 */
export interface SwapChoice {
  slot: number;
  itemId: string;
}

/** A swap the crawler could legally make right now, with the crawler it
 *  produces, so a caller can preview without planning twice. */
export interface SwapOption extends SwapChoice {
  name: string;
  /** The post-swap crawler. Pure - nothing is written. */
  character: Character;
}

/**
 * The free swaps that are legal for one Attack, right now.
 *
 * Both conditions come from the rules: the drawn weapon's Attack must BE the
 * one being made (RULE DCC-HOT-022, C&C p.15), and `planWeaponSwap` must accept
 * the swap against this crawler as they stand. Filtering on "is it a weapon"
 * alone offers swaps the service will refuse, and the player finds out by
 * pressing Attack.
 */
export function swapOptionsFor(
  character: Character,
  attackId: string,
  catalogue: InventoryCatalogue,
): SwapOption[] {
  return character.hotlist.flatMap((entry, slot) => {
    if (entry === null || entry.kind !== 'item') return [];
    const item = catalogue.lookupItem(entry.itemId);
    if (!item || item.weaponAttackId !== attackId) return [];
    const planned = planWeaponSwap(character, slot, catalogue);
    if (!planned.ok) return [];
    return [{ slot, itemId: entry.itemId, name: item.name, character: planned.character }];
  });
}

/**
 * The still-valid option for a remembered choice, or null.
 *
 * BOTH parts must match. Legality answers "could the crawler swap to whatever
 * is in this slot?"; identity answers "is that still the thing the player
 * asked for?" They are different questions, and only the second one keeps a
 * selection meaning what it meant when it was made. If the slot's contents
 * changed - by a swap, a reorder, or an edit out of combat - the choice is
 * gone, and the player picks again rather than being handed a different weapon.
 */
export function resolveSwapChoice(
  options: readonly SwapOption[],
  choice: SwapChoice | null,
): SwapOption | null {
  if (choice === null) return null;
  return (
    options.find(
      (option) => option.slot === choice.slot && option.itemId === choice.itemId,
    ) ?? null
  );
}
